/**
 * Modelo del People-flow para el Panel de Control People: agrupación de pasos
 * por etapa, peor-status por etapa, progreso, y el adapter que suma los pasos
 * IT del pipeline legacy (users.getOnboardingStatus) a la vista unificada.
 */

import type {
  PeopleFlowStage,
  PeopleFlowStep,
  PeopleFlowStepStatus,
} from '@/../../shared/types/people-flow';
import { PEOPLE_FLOW_STAGE_ORDER } from '@/../../shared/types/people-flow';
import { getPipelineSteps, type AltaPipeline } from '@/components/users/OnboardingPipeline';
import type { TFunction } from 'i18next';
import { RESULT_PLAIN } from './plain-language';

export { PEOPLE_FLOW_STAGE_ORDER };
export type { PeopleFlowStage, PeopleFlowStep, PeopleFlowStepStatus };

/** Peor status gana el chip de la etapa. */
const SEVERITY: Record<PeopleFlowStepStatus, number> = {
  error: 7,
  expired: 6,
  blocked_validation: 4,
  waiting: 3,
  scheduled: 2,
  // Por encima de 'ok': una etapa con algo pendiente NO debe mostrarse cerrada.
  ok_pending: 1.5,
  ok: 1,
  na: 0,
};

export function worstStatus(statuses: PeopleFlowStepStatus[]): PeopleFlowStepStatus {
  return statuses.reduce<PeopleFlowStepStatus>(
    (worst, s) => (SEVERITY[s] > SEVERITY[worst] ? s : worst),
    'na'
  );
}

export interface StageSummary {
  stage: PeopleFlowStage;
  steps: PeopleFlowStep[];
  worst: PeopleFlowStepStatus;
  done: number;
  /** Pasos aplicables (excluye 'na'). */
  total: number;
}

export function stageSummaries(steps: PeopleFlowStep[]): StageSummary[] {
  return PEOPLE_FLOW_STAGE_ORDER.map((stage) => {
    const inStage = steps.filter((s) => s.stage === stage);
    const applicable = inStage.filter((s) => s.status !== 'na');
    return {
      stage,
      steps: inStage,
      worst: worstStatus(inStage.map((s) => s.status)),
      // 'ok_pending' cuenta como avance (el paso SÍ corrió): lo que cambia es
      // el color del chip, para que se vea que quedó algo a mano.
      done: applicable.filter((s) => s.status === 'ok' || s.status === 'ok_pending').length,
      total: applicable.length,
    };
  });
}

export type StageTone = 'done' | 'pending_manual' | 'in_progress' | 'failed' | 'empty';

/**
 * Semáforo del chip de etapa (feedback People 31/08): verde = todo hecho,
 * naranja = en curso, rojo = falló o no se llevó a cabo (incluye 'expired').
 * (La pausa manual se sacó el 21/09: nadie pausa nada, Sol.) El detalle por paso sigue viviendo en el popover.
 */
export function stageTone(steps: PeopleFlowStep[]): StageTone {
  const applicable = steps.filter((s) => s.status !== 'na');
  if (applicable.length === 0) return 'empty';
  if (applicable.some((s) => s.status === 'error' || s.status === 'expired')) return 'failed';
  if (applicable.every((s) => s.status === 'ok')) return 'done';
  // Todo corrió pero hay pendientes a mano ⇒ tono propio (verde con anillo
  // ámbar y punto, KP 21/09): el paso SALIÓ, pero alguien tiene que mirar el
  // detalle (p. ej. reservar la salita). Ni verde liso ni ámbar de "en curso".
  if (applicable.every((s) => s.status === 'ok' || s.status === 'ok_pending')) return 'pending_manual';
  return 'in_progress';
}

export function flowProgress(steps: PeopleFlowStep[]): { done: number; total: number } {
  const applicable = steps.filter((s) => s.status !== 'na');
  return {
    done: applicable.filter((s) => s.status === 'ok' || s.status === 'ok_pending').length,
    total: applicable.length,
  };
}

/** Pasos que corrieron pero dejaron algo a mano (para la fila y el resumen). */
export function pendingManualSteps(steps: PeopleFlowStep[]): PeopleFlowStep[] {
  return steps.filter((s) => s.status === 'ok_pending');
}

export function hasFlowError(steps: PeopleFlowStep[]): boolean {
  // 'expired' NO cuenta como error: es "el paso no aplicó" (típico de altas
  // anteriores al motor, marcadas por el seed) — se ve naranja pero no alerta.
  return steps.some((s) => s.status === 'error');
}

// ─── Adapter del pipeline IT legacy ──────────────────────────────────────────

/** Etapa a la que pertenece cada chip del pipeline IT existente. */
const LEGACY_STAGE: Record<string, PeopleFlowStage> = {
  account: 'pre_start',
  mails: 'pre_start',
  people_mails: 'pre_start',
  apple: 'pre_start',
  security: 'day_one',
  enrollment: 'week_one',
};

/**
 * Convierte los 6 chips del pipeline IT (getPipelineSteps) en pasos del flow
 * unificado. `stepId` queda como `legacy_<key>` — la UI los etiqueta con las
 * claves i18n existentes de users.onboarding_page.pipeline.*.
 */
export function legacyStepsFor(pipeline: AltaPipeline | null, hasCorreo: boolean): PeopleFlowStep[] {
  return getPipelineSteps(pipeline, hasCorreo).map((s) => ({
    stepId: `legacy_${s.key}` as PeopleFlowStep['stepId'],
    stage: LEGACY_STAGE[s.key] ?? 'pre_start',
    channel: s.key === 'account' ? 'internal' : 'mail',
    docStep: 'IT',
    status: s.status as PeopleFlowStepStatus,
    dueAt: null,
    executedAt: null,
    error: s.status === 'error' ? s.detail || null : null,
    raw: s.detail || null,
    pending: null,
  }));
}

export function isLegacyStep(step: PeopleFlowStep): boolean {
  return String(step.stepId).startsWith('legacy_');
}

/** Clave i18n del label de un paso (nuevo o legacy). */
export function stepLabelKey(step: PeopleFlowStep): string {
  return isLegacyStep(step)
    ? `users.onboarding_page.pipeline.${String(step.stepId).replace('legacy_', '')}`
    : `users.people_panel.steps.${step.stepId}`;
}

/**
 * Qué quedó a mano en un paso 'ok_pending', en el idioma de la UI: el mismo mapa
 * raw→texto del historial (RESULT_PLAIN en plain-language.ts), único para todo
 * el panel. El texto en español que manda el backend queda de fallback.
 */
export function pendingText(t: TFunction, step: PeopleFlowStep): string {
  const fallback = step.pending ?? '';
  return (step.raw && RESULT_PLAIN(t)[step.raw]) || fallback;
}
